import axios from 'axios';
import {
  FEATURE_DISABLED_CODE,
  FEATURES,
  PROTOTYPE_LIMIT_REACHED_CODE,
  ProjectTransferRefusalCode as Refusal,
  ProjectTransferRequestCode as Request,
  type FeatureKey,
  type ProjectTransferRefusal,
} from '@aiai/shared';
import { errorMessage } from './api';

/** A short, readable heading for each transfer refusal / request code. */
const TITLES: Record<string, string> = {
  [Refusal.SameWorkspace]: 'Already in this workspace',
  [Refusal.SelfProject]: "The platform's feedback project can't move",
  [Refusal.ActiveRun]: 'An agent is still working on it',
  [Refusal.RepoTaken]: 'The repository is already connected there',
  [Refusal.IssueCodeTaken]: 'The issue code is already in use there',
  [Refusal.RepoInaccessible]: "That workspace's GitHub access can't push to the repo",
  [Refusal.DomainConflict]: 'A custom domain would conflict',
  [FEATURE_DISABLED_CODE]: "That workspace's plan is missing a feature",
  [PROTOTYPE_LIMIT_REACHED_CODE]: 'That workspace is at its prototype limit',
  [Request.DestinationRequired]: 'Pick a destination workspace',
  [Request.AmbiguousDestination]: 'That person owns several workspaces — use the workspace slug instead',
  [Request.DestinationNotOwned]: "You don't own that workspace",
  [Request.AlreadyPending]: 'A transfer of this project is already pending',
  [Request.NotPending]: 'This transfer was already answered or cancelled',
  [Request.Expired]: 'This transfer expired',
};

/** Readable heading for a refusal code; unknown codes fall back to the code itself. */
export function transferCodeTitle(code: string, feature?: string): string {
  if (code === FEATURE_DISABLED_CODE && feature && feature in FEATURES) {
    return `That workspace's plan doesn't include ${FEATURES[feature as FeatureKey].label}`;
  }
  return TITLES[code] ?? code;
}

/** "Heading — server detail" for one preflight refusal. */
export function transferRefusalText(r: ProjectTransferRefusal): string {
  const title = transferCodeTitle(r.code, r.feature);
  return r.message && r.message !== title ? `${title} — ${r.message}` : title;
}

/** Readable text for a failed transfer / accept / decline / cancel call. */
export function transferErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as
      | { code?: string; message?: string; feature?: string }
      | undefined;
    if (data?.code && TITLES[data.code]) {
      return transferRefusalText({
        code: data.code,
        message: data.message ?? '',
        feature: data.feature,
        status: 409,
      });
    }
  }
  return errorMessage(err, fallback);
}
